import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";

import {
  Space,
  Button,
  Drawer,
  Progress,
  Card,
} from '@lockerpm/design';

import {
  ExpandOutlined
} from '@ant-design/icons';

import formComponents from '../form';

import { CipherType } from '../../core-js/src/enums';

import global from '../../config/global';
import secretServices from '../../services/secret';
import environmentServices from '../../services/environment';
import coreServices from '../../services/core';

import { getProjectLoadedPercent, timeFromNow, convertDateTime } from '../../utils/common';

function ExportProgress(props) {
  const { ImportExportProgressForm } = formComponents;
  const {
    progress = {},
  } = props
  const { t } = useTranslation();
  const selectedWorkspace = useSelector((state) => state.workspace.selectedWorkspace);

  const [callingAPI, setCallingAPI] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [selectedProjects, setSelectedProjects] = useState([...(progress?.projects || [])])
  const [visible, setVisible] = useState(true);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    handleExport();
  }, [])

  const totalProjects = useMemo(() => {
    return selectedProjects.length
  }, [selectedProjects])

  const totalLoadedProjects = useMemo(() => {
    return selectedProjects.filter((p) => p.percent == 100).length;
  }, [selectedProjects])

  const percent = useMemo(() => {
    const totalSecrets = selectedProjects.map((p) => p.total_secret).reduce((a, b) => a + b, 0);
    const totalEnvironments = selectedProjects.map((p) => p.total_environment).reduce((a, b) => a + b, 0);

    const totalLoadedSecrets = selectedProjects.map((p) => p.pSecrets).flat().length;
    const totalLoadedEnvironments = selectedProjects.map((p) => p.pEnvironments).flat().length;

    return Number(((totalLoadedSecrets + totalLoadedEnvironments) /  (totalSecrets + totalEnvironments) * 100).toFixed(0))
  }, [totalProjects, selectedProjects])

  const getExportData = async () => {
    const allRequests = selectedProjects.map(async (p) => {
      let environments = []
      let environmentsPage = 1;
      let projectPercent = getProjectLoadedPercent(p);
      p.percent = projectPercent;
      const sProjects = selectedProjects.map((project) => {
        if (p.id === project.id) {
          return {
            ...project,
            percent: projectPercent,
          }
        }
        return project
      })
      setSelectedProjects(sProjects);
      while (environments.length < p.total_environment) {
        const response = await environmentServices.list(p.id, { page: environmentsPage, size: global.constants.EXPORT_PAGE_SIZE });
        const results = await coreServices.decrypt_ciphers(response.results, CipherType.Environment);
        environments = [...environments, ...results]
        environmentsPage += 1;
        p.pEnvironments = environments;
        projectPercent = getProjectLoadedPercent(p)
        p.percent = projectPercent;
        const sProjects = selectedProjects.map((project) => {
          if (p.id === project.id) {
            return {
              ...project,
              percent: projectPercent,
              pEnvironments: environments,
            }
          }
          return project
        })
        setSelectedProjects(sProjects);
      }
      let secrets = [];
      let secretsPage = 1;
      while (secrets.length < p.total_secret) {
        const response = await secretServices.list(p.id, { page: secretsPage, size: global.constants.EXPORT_PAGE_SIZE });
        const results = await coreServices.decrypt_ciphers(response.results, CipherType.Secret);
        secrets = [...secrets, ...results]
        secretsPage += 1;
        p.pSecrets = secrets;
        projectPercent = getProjectLoadedPercent(p)
        p.percent = projectPercent;
        const sProjects = selectedProjects.map((project) => {
          if (p.id === project.id) {
            return {
              ...project,
              percent: projectPercent,
              pEnvironments: environments,
              pSecrets: secrets
            }
          }
          return project
        })
        setSelectedProjects(sProjects);
      }
    })
    await Promise.all(allRequests);
    return {
      workspaceName: progress.workspaceName,
      projects: selectedProjects.map((p) => ({
        name: p.name,
        description: p.description,
        secrets: p.pSecrets.map((s) => s.secret),
        environments: p.pEnvironments.map((s) => s.environment),
      }))
    };
  }

  const handleExport = async () => {
    setCallingAPI(true);
    const exportData = await getExportData();
    setExportData(exportData);
    const fileName = global.jsCore.exportService.getFileName(null, progress.format);
    global.jsCore.platformUtilsService.saveFile(
      window,
      JSON.stringify(exportData, null, '  '),
      { type: 'text/plain' },
      fileName
    )
    global.pushSuccess(t('notification.success.import_export.exported'));
    setCallingAPI(false);
    setTimeout(() => {
      setFinished(true);
    }, 10000);
  }

  return (
    <>
      {
        !finished && selectedWorkspace.id == progress.workspaceId && <div className={props.className}>
          <Card
            className='mt-2 border-primary'
            key={progress.id}
            bodyStyle={{ padding: '6px 12px' }}
          >
            <div className='flex items-center justify-between'>
              <p className='font-semibold'>
                {t('import_export.exporting', { project_member: `${totalLoadedProjects}/${totalProjects}` })}
              </p>
              <div className='text-primary cursor-pointer' onClick={() => setVisible(true)}>
                <ExpandOutlined />
              </div>
            </div>
            <Progress className='m-0' percent={percent} />
            <p className='text-xs'>{timeFromNow(progress.createdAt / 1000)}</p>
          </Card>
          <Drawer
            title={t('import_export.export')}
            placement="right"
            onClose={() => setVisible(false)}
            open={visible}
            width={480}
            closable={false}
            footer={
              <Space className='flex items-center justify-end'>
                {
                  selectedProjects && !exportData && <Button
                    type="primary"
                    loading={callingAPI}
                    onClick={() => handleExport()}
                  >
                    { t('button.export') } 
                  </Button>
                }
                {
                  !!exportData && <Button
                    disabled={callingAPI}
                    onClick={() => setFinished(true)}
                  >
                    {t('button.close')}
                  </Button>
                }
              </Space>
            }
          >
            <p className='font-semibold'>
              {t('common.created_time')}: {convertDateTime(progress.createdAt / 1000, 'hh:mm:ss DD-MM-YYYY')}
            </p>
            <ImportExportProgressForm
              selectedProjects={selectedProjects}
            />
          </Drawer>
        </div>
      }
    </>
  );
}

export default ExportProgress;
